import { fromPairs, get, omit } from 'lodash-es';
import moment from 'moment';

import {
  fromServerDate,
  fromServerDateOptional,
  fromServerDateTime,
  fromServerDateTimeAsLocal,
  fromServerDateTimeOptional,
  fromServerDateTimeWithOffsetAsLocal,
  fromServerTime,
  isValidIanaZone,
  isValidUtcOffset,
  joinDateAndTime,
} from '../../common/utils/dateUtils';
import { optionalDecorator } from '../../common/utils/funcUtils';
import { numberOr } from '../../common/utils/numberUtils';
import { trimWords } from '../../common/utils/stringUtils';
import { extractLabelFromRichFormValue } from '../../components/forms/dynamic/dynamicFormHelpers';
import { isFormSectionNonEmpty } from '../../components/forms/formHelpers';
import { richSelectValueToCodeValue } from '../../components/forms/selectUtils';
import { isSavedShipmentModeActive } from '../../forms/shipments/newShipment/savedShipmentsUtils';
import {
  extractActualWeight,
  getPackageCount,
  getPackageTotal,
  getValidPackages,
} from '../../forms/shipments/shipmentCommon';
import { ShipmentStage } from '../enums/ShipmentStage';
import { TransportType } from '../enums/TransportType';
import { normalizeAccount } from './accountConversions';
import {
  DeviceAddonType,
  getDeviceAddonLabel,
  mapDeviceAddonsInput,
} from './deviceAddonConversions';
import {
  formToInputFlight,
  graphQLToFormJobLocation,
} from './locationConversions';
import {
  NewShipmentDateSpecialValue,
  applyNewShipmentDateSpecialValue,
} from './newShipmentConversions';
import { UnitSystem } from '../enums/measuringUnits';

export const graphqlToDetailDateTimeSchedule = optionalDecorator(schedule => ({
  scheduled: fromServerDateTimeOptional(schedule.scheduled),
  isAsap: !!schedule.isAsap,
  estimated: fromServerDateTimeOptional(schedule.estimated),
  actual: fromServerDateTimeOptional(schedule.actual),
}));

export function mapMetadata(metadata, { customerAccounts }) {
  if (!metadata) {
    return null;
  }

  const fullAccount =
    customerAccounts &&
    customerAccounts.find(acc => acc.number === metadata.qolAccount.number);
  const account = fullAccount || normalizeAccount(metadata.qolAccount);

  return {
    account,
    ...omit(metadata, ['qolAccount']),
  };
}

function resolveActivityZones(activity) {
  const ownZone = event => {
    if (isValidIanaZone(event.ianaCode)) {
      return { ianaCode: event.ianaCode };
    }
    if (isValidUtcOffset(event.timezone)) {
      return { offset: event.timezone };
    }
    return null;
  };

  const orderedIndexes = activity
    .map((_, index) => index)
    .sort((a, b) => {
      const dateTimeA = activity[a].dateTime;
      const dateTimeB = activity[b].dateTime;
      if (dateTimeA < dateTimeB) return -1;
      if (dateTimeA > dateTimeB) return 1;
      return 0;
    });

  const zoneByIndex = {};
  let lastZone = null;
  orderedIndexes.forEach(index => {
    lastZone = ownZone(activity[index]) || lastZone;
    zoneByIndex[index] = lastZone;
  });
  lastZone = null;
  for (let i = orderedIndexes.length - 1; i >= 0; i -= 1) {
    const index = orderedIndexes[i];
    lastZone = zoneByIndex[index] || lastZone;
    zoneByIndex[index] = lastZone;
  }
  return zoneByIndex;
}

function mapActivityEvent(ev, zone) {
  return {
    type: ev.type,
    dateTime: fromServerDateTime(ev.dateTime),
    localizedDateTime: zone?.ianaCode
      ? fromServerDateTimeAsLocal(ev.dateTime, zone.ianaCode)
      : fromServerDateTimeWithOffsetAsLocal(ev.dateTime, zone?.offset),
    location: ev.location,
    isHub: ev.isHub,
  };
}

export function mapActivity(activity) {
  if (!activity) {
    return activity;
  }
  const zoneByIndex = resolveActivityZones(activity);
  return activity.map((ev, index) => mapActivityEvent(ev, zoneByIndex[index]));
}

function graphqlToDetail(
  {
    metadata,
    jobNumber,
    quoteNumber,
    bolNumber,
    referenceNo,
    origin,
    destination,
    shipmentStatus,
    stage,
    shippersReferences,
    pickupDateTime,
    deliveryDateTime,
    outForDeliveryDateTime,
    signedBy,
    productExpirationDateTime,
    indicators,
    serviceInformation,
    flights,
    packagesInformation,
    activity,
    driver,
    device,
    serviceUpdate,
    authorizedBy,
    documents,
    assets,
    unitSystem,
    quoteStatus,
    serviceAdvisory,
    charges,
  },
  { customerAccounts } = {}
) {
  return {
    metadata: mapMetadata(metadata, { customerAccounts }),
    jobNumber,
    quoteNumber,
    bolNumber,
    referenceNo,
    authorizedBy,
    origin,
    destination,
    status: shipmentStatus,
    quoteStatus,
    stage,
    shipperReference: trimWords(shippersReferences || []).join('\n'),
    pickupDateTime: graphqlToDetailDateTimeSchedule(pickupDateTime),
    deliveryDateTime: graphqlToDetailDateTimeSchedule(deliveryDateTime),
    outForDeliveryDateTime: fromServerDateOptional(outForDeliveryDateTime),
    signedBy,
    productExpirationDateTime: fromServerDateOptional(
      productExpirationDateTime
    ),
    indicators,
    serviceInformation,
    flights: (flights || []).map(fl => ({
      ...fl,
      tenderDateTime: fromServerDateOptional(fl.tenderDateTime),
      recoveryDateTime: fromServerDateOptional(fl.recoveryDateTime),
      arrival: graphqlToDetailDateTimeSchedule(fl.arrival),
      departure: graphqlToDetailDateTimeSchedule(fl.departure),
    })),
    packagesInformation: packagesInformation && {
      ...packagesInformation,
      packages: packagesInformation.packages.map(pc => ({
        ...pc,
        packoutDateTime: fromServerDateTimeOptional(pc.packoutDateTime),
      })),
    },
    activity: mapActivity(activity),
    driver,
    device,
    serviceUpdate: serviceUpdate && {
      ...serviceUpdate,
      oldEstimatedArrival: fromServerDateTimeOptional(
        serviceUpdate.oldEstimatedArrival
      ),
      newEstimatedArrival: fromServerDateTimeOptional(
        serviceUpdate.newEstimatedArrival
      ),
    },
    documents: documents && {
      bolUrl: documents.bolUrl,
      documentList: documents.documentList?.map(({ uploadedAt, ...doc }) => ({
        ...doc,
        uploadedAt: fromServerDateTimeOptional(uploadedAt),
      })),
    },
    assets,
    unitSystem,
    serviceAdvisory,
    charges,
  };
}

export function formToDetailLocation({ companyName, address, contact } = {}) {
  return {
    companyName,
    address: {
      city: { text: address?.city },
      stateProvince: { text: address?.stateProvince },
      country: { text: address?.country },
      addressLine1: get(address, 'addressLines.0'),
      addressLine2: get(address, 'addressLines.1'),
      addressLine3: get(address, 'addressLines.2'),
      postalCode: address?.postalCode,
    },
    contact: {
      name: contact?.name,
      email: contact?.email,
      phone: contact?.phone,
    },
  };
}

function formToDetailPackage(pkg) {
  const { packoutDate, packoutTime, deviceAddons, ...rest } = pkg;

  return {
    ...rest,
    ...mapDeviceAddonsInput(deviceAddons),
  };
}

function formToDetailPackages(packages) {
  return {
    count: getPackageCount(packages, p => !p.exceptedQuantity),
    totalWeight: getPackageTotal(packages, extractActualWeight),
    packages,
  };
}

function formToDetail(values, { shipperSpecificInfoFields = [] } = {}) {
  const {
    jobNumber,
    quoteNumber,
    account,
    origin,
    destination,
    serviceInformation,
    packageList,
    status,
    stage,
    unitSystem,
  } = values;

  const containsTbd = isSavedShipmentModeActive(values);
  const isAirRetrieval = !!origin?.flight;

  return {
    containsTbd,
    metadata: { account },
    jobNumber: jobNumber || null,
    quoteNumber: quoteNumber || null,
    shipperReference: serviceInformation?.refNo,
    isRetrieval: isAirRetrieval,
    origin: formToDetailLocation(origin),
    retrievalFlight: isAirRetrieval ? formToInputFlight(origin) : undefined,
    destination: formToDetailLocation(destination),
    status: status || 'New',
    stage: stage || containsTbd ? ShipmentStage.SAVED : ShipmentStage.LIVE,
    pickupDateTime: isAirRetrieval
      ? {
          scheduled: moment(origin?.flight?.arrivalDatetime),
          isAsap: false,
        }
      : {
          scheduled: origin.pickupDateTime,
          ...applyNewShipmentDateSpecialValue(
            origin['pickupDateTime-specialValue'],
            origin['pickupDateTime-specialValueData']
          ),
        },
    deliveryDateTime: {
      scheduled: destination.deliveryDateTime,
      ...applyNewShipmentDateSpecialValue(
        destination['deliveryDateTime-specialValue'],
        destination['deliveryDateTime-specialValueData']
      ),
    },
    serviceInformation: {
      // TODO - Right now we cannot determine this, but in the future there may be some way to do it
      transportType: TransportType.FLIGHT,
      commodity: get(serviceInformation, 'commodity'),
      serviceType: richSelectValueToCodeValue(
        get(serviceInformation, 'serviceType')
      ),
      bolNumber: get(serviceInformation, 'bolNumber'),
      dangerousGoods: get(serviceInformation, 'dangerousGoods'),
      exceptedQuantity: get(serviceInformation, 'exceptedQuantity'),
      unNumber: get(serviceInformation, 'unNumber'),
      incoterms: get(serviceInformation, 'incoterms'),
      currency: richSelectValueToCodeValue(get(serviceInformation, 'currency')),
      additionalInsurance: numberOr(
        parseFloat(get(serviceInformation, 'additionalInsurance')),
        null
      ),
      declaredValue: numberOr(
        parseFloat(get(serviceInformation, 'declaredValue')),
        null
      ),
      shipperSpecificInfo: shipperSpecificInfoFields.map(
        ({ name, label, type }) => ({
          label,
          value: extractLabelFromRichFormValue(
            serviceInformation?.shipperSpecificInfo?.[name]
          ),
          type,
        })
      ),
      eoriNumber: serviceInformation?.eoriNumber,
      hsInfo: serviceInformation?.hsInfo?.filter(item =>
        isFormSectionNonEmpty(item)
      ),
    },
    packagesInformation: packageList.tbd
      ? []
      : formToDetailPackages(
          getValidPackages(packageList.packages).map(formToDetailPackage)
        ),
    unitSystem,
  };
}

function graphQLToFormDeviceAddon(deviceAddon, type, index) {
  const text = getDeviceAddonLabel(deviceAddon);
  return {
    device: {
      code: deviceAddon.deviceCode,
      text,
    },
    type,
    brandName: deviceAddon.brandName,
    modelName: deviceAddon.modelName,
    serialNumber: deviceAddon.serialNumber,
    source: deviceAddon.source,
  };
}

function graphQLToFormPackage(pkg) {
  const { packoutDate, packoutTime, gpsDevices, temperatureDevices, ...rest } =
    pkg;

  return {
    ...rest,
    deviceAddons: [
      ...(pkg.gpsDevices?.map((da, i) =>
        graphQLToFormDeviceAddon(da, DeviceAddonType.GPS, i)
      ) || []),
      ...(pkg.temperatureDevices?.map((da, i) =>
        graphQLToFormDeviceAddon(da, DeviceAddonType.TEMPERATURE, i)
      ) || []),
    ],
    packoutDateTime:
      packoutDate &&
      packoutTime &&
      joinDateAndTime(fromServerDate(packoutDate), fromServerTime(packoutTime)),
  };
}

function graphQLToForm(values) {
  const {
    metadata,
    jobNumber,
    origin,
    pickupDateTime,
    destination,
    deliveryDateTime,
    serviceInformation,
    packagesInformation,
    status,
    stage,
    unitSystem,
  } = values;

  return {
    account: normalizeAccount(metadata.qolAccount),
    jobNumber,
    status,
    stage,
    origin: {
      ...graphQLToFormJobLocation(origin),
      pickupDateTime: fromServerDateTimeOptional(pickupDateTime.scheduled),
    },
    destination: {
      ...graphQLToFormJobLocation(destination),
      deliveryDateTime: fromServerDateTimeOptional(deliveryDateTime.scheduled),
      'deliveryDateTime-specialValue': deliveryDateTime.isAsap
        ? NewShipmentDateSpecialValue.ASAP
        : undefined,
    },
    serviceInformation: {
      ...serviceInformation,
      additionalInsurance:
        serviceInformation.additionalInsurance &&
        `${serviceInformation.additionalInsurance}`,
      declaredValue:
        serviceInformation.declaredValue &&
        `${serviceInformation.declaredValue}`,
      shipperSpecificInfo: fromPairs(
        serviceInformation.shipperSpecificInfo.map(({ name, value }) => [
          name,
          value,
        ])
      ),
    },
    packagesInformation: {
      ...packagesInformation,
      packages: getValidPackages(packagesInformation?.packages).map(
        graphQLToFormPackage
      ),
    },
    unitSystem,
  };
}

function quoteToShipmentForm(quote) {
  const {
    origin,
    destination,
    pickupDateTime,
    serviceType,
    commodity,
    unitSystem,
    packages,
  } = quote || {};
  return {
    origin: {
      ...graphQLToFormJobLocation(origin),
      pickupDateTime: fromServerDateTimeOptional(pickupDateTime?.scheduled),
      'pickupDateTime-date': null,
      'pickupDateTime-time': null,
      'pickupDateTime-specialValue': pickupDateTime?.isNow
        ? NewShipmentDateSpecialValue.NOW
        : undefined,
    },
    destination: graphQLToFormJobLocation(destination),
    serviceInformation: {
      serviceType,
      commodity,
      isMetric: unitSystem === UnitSystem.METRIC,
      dangerousGoods: quote?.dangerousGoods,
      exceptedQuantity: quote?.exceptedQty,
      unNumber: quote?.unNumber,
    },
    packageList: {
      packages: packages?.map(pkg => ({
        packagingType: pkg?.packagingType,
        packageSource: pkg?.packageSource,
        actualWeight: pkg?.actualWeight,
        length: pkg?.length,
        width: pkg?.width,
        height: pkg?.height,
        dimensionalWeight: pkg?.dimensionalWeight, // TODO: Not on get quote request
        billedWeight: pkg?.billedWeight, // TODO: Not on get quote request
      })),
    },
    unitSystem,
  };
}

export const shipmentConversions = {
  graphqlToDetail,
  formToDetail,
  graphQLToForm,
  quoteToShipmentForm,
};
